<template>
  <div class="d-flex" style="min-height: 45vw;">
    <div class="content p-4 w-100">
      <div class="d-flex">
        <h4 class="card-title mt-0">
          {{ i18n.global.t('pages.deviceFirmware.title') }}
        </h4>
      </div>
      <div class="mb-5 d-flex justify-start">
        <div>
          <v-btn
            v-if="updateAuthority"
            color="error"
            @click="createRequestAndDetachFirmwareToDevices"
            :disabled="isDetachButtonDisabled"
          >
            {{ detachBtnText }}
          </v-btn>
        </div>
      </div>
      <device-list
        class="mb-2"
        :devices="devices"
        :isDeviceListLoading="isDeviceListLoading"
        @sort-change="handleChangeSort"
        @update-selected-items="updateSelectedItems"
      />
      <template v-if="allDevicesCount > 0">
        <div class="d-flex align-center justify-end">
            <v-combobox
              class="mr-4 combobox"
              :label="i18n.global.t('pages.deviceFirmware.resultsPerPage')"
              :items="devicesPerPageVariants"
              v-model="devicesPerPage"
              :hide-details="true"
            />
            <PaginateWithCount
              class="justify-end"
              v-model="currentPage"
              :totalPage="totalPage"
              :itemsPerPage="devicesPerPage"
              :allElementsCount="allDevicesCount"
              :showItemCount="true"
            />
          <hr>
        </div>
        <hr>
      </template>
      <attach-firmware-form
        :isFirmwareDeviceAttaching="isFirmwareDeviceAttaching"
        :updateAuthority="updateAuthority"
        :isAttachButtonDisabled="isAttachButtonDisabled"
        :availableToSelectFirmwares="availableToSelectFirmwares"
        @send-form="createRequestAndAttachFirmwareToDevices"
      />
    </div>
    <device-list-search-form
      :isDeviceListLoading="isDeviceListLoading"
      @form-change="handleChangeSearchForm"
      @search="resetPaginationAndGetDevices"
    />
  </div>
</template>

<script lang="ts">

import {defineComponent} from "vue";
import Api from "@/services/api/ApiServiceFabrick";
import {getDeviceFirmwareListRequest, perPage, sortField} from "@/types/requests/getDeviceFirmwareListRequest";
import {
  calculatePageCount,
  formatDate
} from "@/services/functions";
import DeviceFirmware from "@/types/DeviceFirmware";
import DeviceList from "@/components/Firmware/DeviceList.vue";
import PaginateWithCount from "@/components/PaginateWithCount.vue";
import { sortType, DEVICE_FIRMWARE_DEFAULT_DAYS_RANGE } from "@/services/consts";
import DeviceListSearchForm from "@/components/Firmware/DeviceListSearchForm.vue";
import AttachFirmwareForm from "@/components/Firmware/AttachFirmwareForm.vue";
import {attachFirmwareToDevicesRequest} from "@/types/requests/attachFirmwareToDevicesRequest";
import {detachFirmwareFromDevicesRequest} from "@/types/requests/detachFirmwareFromDevicesRequest";
import getFirmwareVersionsResponse from "@/types/responses/getFirmwareVersionsResponse";
import AuthorityCacheApi from "@/services/models/AuthorityCacheApi";
import i18n from '@/lang/i18n';
export default defineComponent({
  data() {
    const currentDate = new Date()
    const defaultStartOfRange = new Date(currentDate)
    defaultStartOfRange.setDate(defaultStartOfRange.getDate() - DEVICE_FIRMWARE_DEFAULT_DAYS_RANGE)

    return {
      currentPage: 1 as number,
      totalPage: 0 as number,
      devices: [] as Array<DeviceFirmware>,
      deviceListFilter: {
        version: undefined as undefined | string,
        device_id: undefined as undefined | number,
        ar_device_type_ids: undefined as undefined | number[],
        device_operation_office_companies: undefined as undefined | number[],
        firmware_id: undefined as undefined | number,
        vehicle_id: undefined as undefined | number,
        activationDateRange: [defaultStartOfRange, currentDate] as Date[],
      },
      deviceListSort: {
        sort_type: undefined as sortType | undefined,
        sort: undefined as sortField | undefined,
      },
      isDeviceListLoading: false as boolean,
      selectedDevices: [] as Array<string>,
      isFirmwareDeviceAttaching: false as boolean,
      isFirmwareDeviceDetaching: false as boolean,
      devicesPerPage: 50 as number,
      allDevicesCount: 0 as number,
      firmwares: [] as getFirmwareVersionsResponse,
      updateAuthority: true as boolean,
    }
  },
  props: {
    showFlash: {
      type: Function,
      required: true,
    },
    loginWithRedirect: {
      type: Function,
      required: true,
    },
  },
  name: "DeviceFirmwareView",
  setup() {
    document.title =  i18n.global.t('pages.deviceFirmware.title')
    return {
      i18n
    }
  },
  mounted() {
    this.getAuthorities()
    this.getDevices()
    this.getFirmwares()
  },
  watch: {
    currentPage: {
      handler(newValue, oldValue): void {
        if (newValue !== oldValue) {
          this.getDevices()
        }
      }
    },
    devicesPerPage: {
      handler(): void {
        this.getDevices()
      }
    }
  },
  computed: {
    getDevicesListRequest(): getDeviceFirmwareListRequest {

      const request = {
        page: this.currentPage,
        per_page: this.devicesPerPage,
      } as getDeviceFirmwareListRequest;

      if (this.deviceListFilter.version) {
        request.version = this.deviceListFilter.version
      }

      if (this.deviceListFilter.device_id) {
        request.device_id = this.deviceListFilter.device_id
      }

      if (this.deviceListFilter.ar_device_type_ids) {
        request.ar_device_type_ids = this.deviceListFilter.ar_device_type_ids
      }

      if (this.deviceListFilter.device_operation_office_companies) {
        request.device_operation_office_companies = this.deviceListFilter.device_operation_office_companies
      }

      if (this.deviceListFilter.firmware_id) {
        request.firmware_id = this.deviceListFilter.firmware_id
      }

      if (this.deviceListFilter.vehicle_id) {
        request.vehicle_id = this.deviceListFilter.vehicle_id
      }

      if (this.deviceListFilter.activationDateRange) {
        request.activation_date_range_start = formatDate(this.deviceListFilter.activationDateRange[0])
        request.activation_date_range_end = formatDate(this.deviceListFilter.activationDateRange[1])
      }

      if (this.deviceListSort.sort_type && this.deviceListSort.sort) {
        request.sort = this.deviceListSort.sort;
        request.sort_type = this.deviceListSort.sort_type;
      }

      return request
    },
    detachBtnText(): string {
      return this.isFirmwareDeviceDetaching ?
      i18n.global.t('pages.deviceFirmware.button.deleting') :
      i18n.global.t('pages.deviceFirmware.button.delete');
    },
    devicesPerPageVariants(): Array<number> {
      return [perPage.ITEM_50, perPage.ITEM_100, perPage.ITEM_200]
    },
    selectedFirmwareRelationIds(): Array<number> {
      const result: Array<number> = []
      this.devices.map(device => {
        const key = this.makeKey(device)
        if (this.selectedDevices.includes(key) && device.firmware?.id) {
          result.push(device.firmware?.firmware_device_relation_id)
        }
      });
      return result
    },
    selectedDeviceTypes(): Array<number> {
      const result: Array<number> = []
      this.devices.map(device => {
        const key = this.makeKey(device)
        if (this.selectedDevices.includes(key) && device.type?.id) {
          result.push(device.type?.id)
        }
      });
      return [...new Set(result)]
    },
    availableToSelectFirmwares(): getFirmwareVersionsResponse {
      if (this.selectedDeviceTypes.length > 0) {

        return this.firmwares.filter(firmware => {
          for ( let i=0; i<this.selectedDeviceTypes.length; i++ ){
            if ( !firmware.device_type_ids.includes(this.selectedDeviceTypes[i]) ){
              return false
            }
          }
          return true
        });

      } else {
        return this.firmwares
      }
    },
    isDetachButtonDisabled(): boolean {
      return this.selectedFirmwareRelationIds.length < 1
    },
    isAttachButtonDisabled(): boolean {
      return this.selectedDevices.length < 1
    },
  },
  methods: {
    makeKey(device: DeviceFirmware): string {
      const firmwareId = device?.firmware?.id;
      const deviceId = device.id;
      return `${deviceId}.${firmwareId}`;
    },
    findDeviceByKey(key: string): DeviceFirmware|undefined {
      const deviceId = key.split('.')[0];
      return this.devices.find((device) => device.id == deviceId)
    },
    getSelectedDeviceIds(): string[] {
      return this.selectedDevices.map((key) => this.findDeviceByKey(key)?.id ?? '');
    },
    async getAuthorities(): Promise<void> {
      const authorityList = await AuthorityCacheApi.getWithParse();
      this.updateAuthority = authorityList["octlink.firmware.update"];
    },
    getDevices(): void {
      this.isDeviceListLoading = true

      Api().getDeviceFirmwareList(this.getDevicesListRequest).then((data) => {
        this.devices = data.devices
        this.totalPage = calculatePageCount(data.pagination.all_elements_count, data.pagination.elements_per_page)
        this.isDeviceListLoading = false
        this.allDevicesCount = data.pagination.all_elements_count
      }).catch((error) => {
        const res = error.response
        switch (res.status) {
          case 401:
            this.loginWithRedirect({appState: {targetUrl: location.pathname}})
            break
          case 403:
            this.$router.push({
              name: 'Dashboard',
            })
            break
          case 422:
            this.showFlash(i18n.global.t('pages.deviceFirmware.errors.getDeviceList'), "error", false)
        }
        this.isDeviceListLoading = false
      })
    },
    getFirmwares(): void {
      Api().autoCompleteFirmwaresVersions().then((data) => {
        this.firmwares = data
      })
    },
    handleChangeSort(sortParams: { sort_type: sortType | undefined, sort: sortField | undefined }): void {
      this.deviceListSort = sortParams
      this.getDevices()
    },
    handleChangeSearchForm(form: {
      version: string | undefined,
      device_id: number | undefined,
      ar_device_type_ids: number[] | undefined,
      device_operation_office_companies: number[] | undefined,
      firmware_id: number | undefined,
      vehicle_id: number | undefined,
      activationDateRange: Date[],
    }): void {
      this.deviceListFilter = form
    },
    updateSelectedItems(selectedDevices: Array<string>): void {
      this.selectedDevices = selectedDevices
    },
    createRequestAndAttachFirmwareToDevices(params: { firmware_id: number, activate_date: Date }): void {
      const request: attachFirmwareToDevicesRequest | undefined = this.createAttachFirmwareToDevices(params)
      if (request) {
        this.attachFirmwareToDevices(request)
      }
    },
    createAttachFirmwareToDevices(params: { firmware_id: number, activate_date: Date }): attachFirmwareToDevicesRequest | undefined {
      if (this.selectedDevices.length < 1) {
        return
      }
      return {
        firmware_id: params.firmware_id,
        activate_date: formatDate(params.activate_date),
        ar_device_ids: this.getSelectedDeviceIds()
      }
    },
    attachFirmwareToDevices(request: attachFirmwareToDevicesRequest): void {

      this.isFirmwareDeviceAttaching = true

      Api().attachFirmwareToDevices(request).then(() => {
        this.isFirmwareDeviceAttaching = false
        this.getDevices()
      }).catch((error) => {
        const res = error.response
        switch (res.status) {
          case 401:
            this.loginWithRedirect({appState: {targetUrl: location.pathname}})
            break
          case 403:
            this.$router.push({
              name: 'Dashboard',
            })
            break
          case 422:
            this.showFlash(i18n.global.t('pages.deviceFirmware.errors.applyFirmware'), "error", false)
            break
          case 400:
            this.showFlash(res.data?.message, "error", false)
            break
        }
        this.isFirmwareDeviceAttaching = false
      })
    },
    createRequestAndDetachFirmwareToDevices(): void {
      if (this.isFirmwareDeviceDetaching) {
        return
      }
      const request: detachFirmwareFromDevicesRequest | undefined = this.createDetachFirmwareToDevicesRequest()
      if (request) {
        this.detachFirmwareFromDevices(request)
      }
    },
    createDetachFirmwareToDevicesRequest(): detachFirmwareFromDevicesRequest | undefined {
      if (this.selectedFirmwareRelationIds.length < 1) {
        return
      }

      return {
        ar_relations_ids: this.selectedFirmwareRelationIds
      }
    },
    detachFirmwareFromDevices(request: detachFirmwareFromDevicesRequest): void {

      this.isFirmwareDeviceDetaching = true

      Api().detachFirmwareFromDevices(request).then(() => {
        this.isFirmwareDeviceDetaching = false
        this.getDevices()
      }).catch((error) => {
        const res = error.response
        switch (res.status) {
          case 401:
            this.loginWithRedirect({appState: {targetUrl: location.pathname}})
            break
          case 403:
            this.$router.push({
              name: 'Dashboard',
            })
            break
          case 422:
            this.showFlash(i18n.global.t('pages.deviceFirmware.errors.deleteFirmware'), "error", false)
            break
          case 400:
            this.showFlash(res.data?.message, "error", false)
            break
        }
        this.isFirmwareDeviceDetaching = false
      })
    },
    resetPaginationAndGetDevices(): void {
      this.resetPagination()
      this.getDevices()
    },
    resetPagination(): void {
      this.currentPage = 1
    },
    getFirmwareMemo(firmwareID: number | undefined): string | undefined {
      if (!firmwareID) {
        return
      }
      const firmware = this.firmwares.find(firmware => firmware.id == firmwareID)
      if (!firmware) {
        return
      }
      return firmware.memo;
    },
  },
  components: {
    DeviceList,
    PaginateWithCount,
    DeviceListSearchForm,
    AttachFirmwareForm,
  }
})
</script>

<style scoped>
.content {
  min-width: 58vw;
}
.combobox {
  max-width: 160px;
}
</style>
